<script setup>
// ========================================
// Imports
// ========================================
import { useDisplay } from 'vuetify'
import { refDebounced } from '@vueuse/core'
import SalonerLogo from '@images/logo.svg?skipsvgo' // SVGO strips the viewBox, so a resized logo gets cropped
import TabHeader from '@/components/TabHeader.vue'
import DatePicker from '@/components/DatePicker.vue'
import DateTimeChip from '@/components/chips/DateTimeChip.vue'
import EmployeeAvatar from '@/components/EmployeeAvatar.vue'
import TablePaginationComplete from '@/components/pagination/TablePaginationComplete.vue'
import CommunicationNotice from '@/components/messages/CommunicationNotice.vue'
import { useCalendarStore } from '@/views/apps/calendar/useCalendarStore'
import { useCustomerStore } from '@/stores/CustomersStore'
import { useModalStore } from '@/stores/ModalStore'
import { extractDate, extractDateFromString, extractTimeFromDateString } from '@/utils/datetime'
import { formatPhone, isPhoneNumber } from '@/utils/strings'
import { SMS_STATUS_TONES } from '@/utils/sms'

// Keeps "Communication" highlighted in the menu
definePage({ meta: { navActiveLink: 'communication' } })

// ========================================
// Composables & Stores
// ========================================
const { t, te } = useI18n()
const { mobile, mdAndDown, xlAndUp } = useDisplay()
const calendarStore = useCalendarStore()
const customerStore = useCustomerStore()
const modalStore = useModalStore()

// ========================================
// State
// ========================================
const tab = ref('outgoing') // outgoing | incoming - also the endpoint name
const phone = ref('')
const state = ref(null) // outgoing only: pending | sent | delivered | failed
const dateFrom = ref(null)
const dateTo = ref(null)
const page = ref(1)
const perPage = ref(20)
const messages = ref([])
const meta = ref({})
const isLoading = ref(true)
const notice = ref(null) // data.notice from BE - shown above the content
const openRows = ref({}) // mobile: message id -> details expanded

const isOutgoing = computed(() => tab.value === 'outgoing')

// ========================================
// Filters
// ========================================
// BE matches the number as a prefix and refuses fewer than 6 digits - shorter input is not sent
const PHONE_MIN_DIGITS = 6
const countDigits = value => (value || '').replace(/\D/g, '').length

// Only digits, spaces and "+" - letters (also pasted ones, e.g. "tel. 796-818-777") never get in.
// flush: 'post' - cleaned after the typed value is rendered, otherwise "12" -> "12a" -> "12" looks unchanged
// to Vue and the "a" stays visible in the field
watch(phone, value => {
  const clean = (value || '').replace(/[^\d+ ]/g, '').trimStart()
  if (clean !== value) phone.value = clean
}, { flush: 'post' })

const debouncedPhone = refDebounced(phone, 400)
const phoneQuery = computed(() => countDigits(debouncedPhone.value) >= PHONE_MIN_DIGITS ? debouncedPhone.value.trim() : '')
// const phoneHint = computed(() => {
//   const digits = countDigits(phone.value)
//
//   return digits && digits < PHONE_MIN_DIGITS ? t('messages_page.history.phone_min_digits') : ''
// })

// State groups of BE (SMS_LIST_STATES) - not the 7 statuses, a single gateway code is too fine-grained for a dropdown
const stateOptions = computed(() => ['pending', 'sent', 'delivered', 'failed'].map(value => ({
  title: t(`messages_page.history.state.${value}`),
  value,
})))

// Every filter but the page - any change starts again from page 1
const filterQuery = computed(() => {
  const query = { perPage: perPage.value }
  if (phoneQuery.value) query.phone = phoneQuery.value
  if (isOutgoing.value && state.value) query.state = state.value
  if (dateFrom.value) query.from = extractDate(new Date(dateFrom.value), '-')
  if (dateTo.value) query.to = extractDate(new Date(dateTo.value), '-')

  return query
})

const hasActiveFilters = computed(() => !!(phone.value || (isOutgoing.value && state.value) || dateFrom.value || dateTo.value))

// Mobile: filters hidden behind a button - the badge counts the ones set in that panel
const showFilters = ref(false)
const panelFiltersCount = computed(() => [isOutgoing.value && state.value, dateFrom.value, dateTo.value].filter(Boolean).length)

const clearFilters = () => {
  phone.value = ''
  state.value = null
  dateFrom.value = null
  dateTo.value = null
}

// ========================================
// Messages
// ========================================
let lastRequestId = 0

const fetchMessages = async () => {
  const requestId = ++lastRequestId
  isLoading.value = true

  try {
    const response = await $backend(`/communication/${tab.value}`, {
      method: 'GET',
      query: { ...filterQuery.value, page: page.value },
    })
    if (requestId !== lastRequestId || !response?.success) return

    messages.value = response.data?.messages || []
    meta.value = response.meta || {}
    notice.value = response.data?.notice || null
    openRows.value = {}
  } catch (e) {
    // aborted or failed - $backend already showed the error
  } finally {
    if (requestId === lastRequestId) isLoading.value = false
  }
}

// The other tab's rows don't fit the new columns
watch(tab, () => { messages.value = [] })

watch([tab, filterQuery], () => {
  if (page.value === 1) fetchMessages()
  else page.value = 1 // the page watcher fetches
})

watch(page, fetchMessages)

// ========================================
// Table
// ========================================
const headers = computed(() => [
  { title: t('messages_page.history.date_time'), key: 'dateTime', sortable: false },
  { title: t('messages_page.history.client'), key: 'client', sortable: false },
  ...(isOutgoing.value ? [{ title: t('messages_page.history.sender'), key: 'sender', sortable: false }] : []),
  { title: t('messages_page.history.content'), key: 'message', sortable: false, cellProps: { class: { 'history-message-cell': !mobile.value } } },
  ...(isOutgoing.value
    ? [{ title: t('status'), key: 'status', sortable: false, align: 'start' }]
    : [{ title: t('messages_page.history.details'), key: 'details', sortable: false }]),
])

// sender.userId matches employeeId of calendar employees and deleted employees
const employeesById = computed(() => Object.fromEntries(
  [...calendarStore.availableCalendars, ...(calendarStore.unavailableEmployees || [])]
    .filter(employee => employee.employeeId)
    .map(employee => [String(employee.employeeId), employee]),
))

// "2026-09-24 10:41:25" -> "24.09.2026 10:41"
const formatDate = dateTime => extractDateFromString(dateTime).split('-').reverse().join('.')
const formatDateTime = dateTime => `${formatDate(dateTime)} ${extractTimeFromDateString(dateTime)}`

const rows = computed(() => messages.value.map(message => {
  // Queued messages have no conversationTime yet - only when they are due
  const dateTime = message.plannedTime || message.conversationTime || ''
  const senderType = message.sender?.type === 'employee' ? 'employee' : 'system'
  const status = message.status
  const tone = SMS_STATUS_TONES[status?.tone]

  return {
    ...message,
    date: dateTime && formatDate(dateTime),
    time: dateTime && extractTimeFromDateString(dateTime),
    isPlanned: !!message.plannedTime,
    senderType,
    employee: senderType === 'employee' ? employeesById.value[String(message.sender?.userId)] : null,
    statusView: status && {
      label: te(`messages_page.status.${status.key}`) ? t(`messages_page.status.${status.key}`) : status.label,
      color: tone?.color,
      icon: tone?.icon,
    },
    readAt: message.readTime && formatDateTime(message.readTime),
  }
}))

const openCustomerCard = async salonerClientId => {
  await customerStore.setSelectedCustomer({ salonerClientId })
  await nextTick()
  modalStore.openModal('customerCardDialog')
}

onMounted(() => {
  // Employees for message senders - the calendar page loads them, other pages may not have them yet
  if (!calendarStore.availableCalendars.length) calendarStore.updateCalendarEmployeesAndResources()

  fetchMessages()
})
</script>

<template>
  <TabHeader
    :title="$t('messages_page.history.title')"
    :breadcrumbs="[
      { title: $t('communication'), to: '/communication' },
      { title: $t('messages_page.history.title'), to: '/communication/history' },
    ]"
  >
    <span class="ml-auto">
      <VBtn
        variant="outlined"
        color="secondary"
        :size="mobile ? 'small' : 'default'"
        to="/communication"
      >
        <VIcon
          icon="ri-arrow-left-line"
          size="18"
          :class="mobile ? '' : 'mr-1'"
        />
        <span v-if="!mobile">{{ $t('back') }}</span>
      </VBtn>
      <VTooltip
        v-if="!mobile"
        activator="parent"
      >
        {{ $t('back') }}
      </VTooltip>
    </span>
  </TabHeader>

  <VContainer
    class="mb-4"
    :class="{
      'px-0': mdAndDown,
      'px-15': !mdAndDown,
    }"
    fluid
  >
    <CommunicationNotice :notice="notice" />

    <VCard
      variant="elevated"
      :class="mobile ? 'mx-n4' : ''"
    >
      <div :class="mobile ? 'pa-3 pb-0' : 'pa-5 pb-0'">
        <!-- 👉 Direction tabs -->
        <VTabs
          id="history-tabs"
          v-model="tab"
          color="primary"
          :grow="mobile"
        >
          <VTab
            value="outgoing"
            :class="{ 'px-1': mobile }"
          >
            <VIcon
              start
              icon="ri-arrow-right-up-line"
            />
            {{ $t('messages_page.history.tab_outgoing') }}
          </VTab>
          <VTab
            value="incoming"
            :class="{ 'px-1': mobile }"
          >
            <VIcon
              start
              icon="ri-arrow-left-down-line"
            />
            {{ $t('messages_page.history.tab_incoming') }}
          </VTab>
        </VTabs>
        <VDivider :class="mobile ? 'mb-4' : 'mb-6'" />

        <!-- 👉 Filters - on mobile only the phone is visible, the rest is behind the "Filters" button -->
        <div
          class="d-flex flex-wrap align-start mb-4"
          :class="mobile ? 'ga-3' : 'ga-4'"
        >
          <VTextField
            v-model="phone"
            :label="$t('messages_page.history.phone')"
            :placeholder="$t('type_at_least_6nums')"
            prepend-inner-icon="ri-search-line"
            color="secondary"
            variant="outlined"
            density="compact"
            inputmode="tel"
            maxlength="20"
            clearable
            hide-details="auto"
            class="filter-field flex-1-1-0"
          />
          <VBtn
            v-if="mobile"
            variant="outlined"
            :color="showFilters || panelFiltersCount ? 'primary' : 'secondary'"
            :active="showFilters"
            prepend-icon="ri-equalizer-line"
            class="flex-0-0-auto"
            style="min-height: 40px;"
            @click="showFilters = !showFilters"
          >
            {{ $t('filters') }}
            <VBadge
              v-if="panelFiltersCount"
              :content="panelFiltersCount"
              color="primary"
              class="ml-2"
              inline
            />
          </VBtn>
          <template v-if="!mobile || showFilters">
            <VSelect
              v-if="isOutgoing"
              v-model="state"
              :label="$t('status')"
              :items="stateOptions"
              color="secondary"
              variant="outlined"
              density="compact"
              clearable
              hide-details
              class="filter-field"
              :class="mobile ? 'w-100' : 'flex-1-1-0'"
            />
            <DatePicker
              v-model="dateFrom"
              :label-text="$t('messages_page.history.date_from')"
              :max-date="dateTo"
              clearable
              teleport
              class="filter-field flex-1-1-0"
            />
            <DatePicker
              v-model="dateTo"
              :label-text="$t('messages_page.history.date_to')"
              :min-date="dateFrom"
              clearable
              teleport
              class="filter-field flex-1-1-0"
            />
            <VBtn
              variant="outlined"
              :disabled="!hasActiveFilters"
              :color="hasActiveFilters ? 'primary' : 'gray'"
              :block="mobile"
              class="px-2 ml-auto flex-0-0-auto"
              style="min-height: 40px;"
              @click="clearFilters"
            >
              <template #prepend>
                <VIcon
                  size="18"
                  icon="ri-filter-off-line"
                  :class="mobile || xlAndUp ? '' : 'mr-n2'"
                />
              </template>
              <span v-if="mobile || xlAndUp">{{ $t('clear_filters') }}</span>
            </VBtn>
          </template>
        </div>
      </div>

      <!-- 👉 Messages - outside the padding, edge to edge like on /clients -->
      <VDataTableServer
        class="custom-table extra-x-paddings text-high-emphasis"
        :striped="mobile ? null : 'even'"
        :headers="headers"
        :items="rows"
        :items-length="meta.total || 0"
        :items-per-page="perPage"
        :loading="isLoading"
        :mobile="mobile"
        :hide-default-header="mobile"
        :hover="!mobile"
        :no-data-text="$t('no_matching_results')"
        item-value="id"
        hide-default-footer
      >
        <template #loading>
          <VSkeletonLoader type="table-row@5" />
        </template>

        <!-- 👉 Mobile - one card per message, like on /storage/product-list -->
        <template
          v-if="mobile"
          #item="{ item }"
        >
          <tr>
            <td class="history-mobile-cell pa-3">
              <div class="d-flex flex-column ga-2">
                <div class="d-flex align-center justify-space-between ga-2">
                  <div class="d-flex flex-column flex-shrink-0">
                    <span
                      v-if="item.isPlanned"
                      class="text-caption font-weight-medium text-warning"
                    >
                      {{ $t('messages_page.history.planned_for') }}
                    </span>
                    <DateTimeChip
                      :date="item.date"
                      :time="item.time"
                    />
                  </div>
                  <!-- A long status is cut with "..." so the row stays one line and the toggle stays visible -->
                  <div class="mobile-status d-flex align-center justify-end ga-1">
                    <VChip
                      v-if="isOutgoing && item.statusView"
                      size="small"
                      :color="item.statusView.color"
                      :prepend-icon="item.statusView.icon"
                    >
                      <span class="text-truncate">{{ item.statusView.label }}</span>
                    </VChip>
                    <VChip
                      v-else-if="!isOutgoing"
                      size="small"
                      :color="item.readAt ? undefined : 'primary'"
                      :prepend-icon="item.readAt ? 'ri-check-line' : 'ri-mail-unread-line'"
                    >
                      <span class="text-truncate">{{ item.readAt ? $t('messages_page.history.read_in_panel') : $t('messages_page.history.unread') }}</span>
                    </VChip>
                    <!-- Same toggle as on /clients mobile cards -->
                    <VBtn
                      color="gray"
                      variant="text"
                      class="flex-shrink-0"
                      :append-icon="openRows[item.id] ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'"
                      :aria-label="$t('messages_page.history.details')"
                      @click="openRows[item.id] = !openRows[item.id]"
                    >
                      <template #append>
                        <VIcon
                          class="ml-n2"
                          size="24"
                        />
                      </template>
                    </VBtn>
                  </div>
                </div>

                <div class="d-flex align-baseline justify-space-between ga-2">
                  <a
                    v-if="item.salonerClientId"
                    href="#"
                    class=" text-high-emphasis text-primary"
                    @click.prevent="openCustomerCard(item.salonerClientId)"
                  >
                    {{ item.clientName || "" }}
                  </a>
                  <span
                    v-if="item.clientPhone"
                    class="text-caption text-medium-emphasis text-no-wrap ml-auto"
                  >
                    {{ formatPhone(item.clientPhone) }}
                  </span>
                </div>

                <div
                  class="text-body-2 text-medium-emphasis text-pre-wrap text-break"
                  :class="{ 'line-clamp-2': !openRows[item.id] }"
                >
                  {{ item.message }}
                </div>

                <div
                  v-if="openRows[item.id]"
                  class="d-flex flex-column ga-1 pa-3 rounded bg-background text-caption"
                >
                  <div
                    v-if="isOutgoing"
                    class="d-flex justify-space-between ga-3"
                  >
                    <span class="text-medium-emphasis">{{ $t('messages_page.history.sender') }}</span>
                    <span class="d-flex align-center ga-1 font-weight-medium">
                      <template v-if="item.senderType === 'employee'">
                        {{ item.employee ? item.employee.title || item.employee.fullName : $t('employee') }}
                      </template>
                      <template v-else>Saloner</template>
                    </span>
                  </div>
                  <div
                    v-if="item.salonerSender"
                    class="d-flex justify-space-between ga-3"
                  >
                    <span
                      v-if="isOutgoing"
                      class="text-medium-emphasis"
                    >
                      {{ $t(isPhoneNumber(item.salonerSender) ? 'messages_page.history.from_number' : 'messages_page.history.from_override') }}
                    </span>
                    <span
                      v-else
                      class="text-medium-emphasis"
                    >
                      {{ $t(isPhoneNumber(item.salonerSender) ? 'messages_page.history.to_number' : 'messages_page.history.from_override') }}
                    </span>
                    <span class="font-weight-medium">{{ isPhoneNumber(item.salonerSender) ? formatPhone(item.salonerSender) : item.salonerSender }}</span>
                  </div>
                  <div
                    v-if="!isOutgoing"
                    class="d-flex justify-space-between ga-3"
                  >
                    <span class="text-medium-emphasis">{{ $t('messages_page.history.read_in_panel') }}</span>
                    <span class="font-weight-medium">{{ item.readAt || '–' }}</span>
                  </div>
                </div>
              </div>
            </td>
          </tr>
        </template>

        <template #item.client="{ item }">
          <div class="d-flex flex-column py-2 pr-4">
            <a
              v-if="item.salonerClientId"
              href="#"
              class="font-weight-medium text-high-emphasis text-no-wrap text-primary"
              @click.prevent.stop="openCustomerCard(item.salonerClientId)"
            >
              {{ item.clientName || "" }}
            </a>
            <span
              v-if="item.clientPhone"
              class="text-caption text-medium-emphasis text-no-wrap"
            >
              {{ formatPhone(item.clientPhone) }}
            </span>
          </div>
        </template>

        <template #item.status="{ item }">
          <div class="d-flex justify-end">
            <VChip
              v-if="item.statusView"
              size="small"
              :color="item.statusView.color"
              :prepend-icon="item.statusView.icon"
              class="text-no-wrap"
            >
              {{ item.statusView.label }}
            </VChip>
          </div>
        </template>

        <template #item.sender="{ item }">
          <div class="d-flex flex-column py-2 pr-4">
            <div class="d-flex align-center ga-2 text-no-wrap pr-4">
              <template v-if="item.senderType === 'employee'">
                <EmployeeAvatar
                  v-if="item.employee"
                  :employee="item.employee"
                  size="mico"
                  :show-tooltip="false"
                />
                {{ item.employee ? item.employee.title || item.employee.fullName : $t('employee') }}
              </template>
              <template v-else>
                <SalonerLogo
                  width="14"
                  height="14"
                />
                Saloner
              </template>
            </div>
            <div
              v-if="item.salonerSender"
              class="text-caption text-medium-emphasis text-no-wrap"
            >
              {{ isPhoneNumber(item.salonerSender) ? formatPhone(item.salonerSender) : item.salonerSender }}
            </div>
          </div>

        </template>

        <template #item.message="{ item }">
          <div class="text-body-2 text-medium-emphasis text-pre-wrap text-break py-2">
            {{ item.message }}
          </div>
        </template>

        <template #item.dateTime="{ item }">
          <div class="d-flex flex-column text-no-wrap pr-4">
            <span
              v-if="item.isPlanned"
              class="text-caption font-weight-medium text-warning"
            >
              {{ $t('messages_page.history.planned_for') }}
            </span>
            <DateTimeChip
              :date="item.date"
              :time="item.time"
            />
          </div>
        </template>

        <!-- Incoming only -->
        <template #item.details="{ item }">
          <div class="d-flex flex-column py-2 text-caption text-medium-emphasis text-no-wrap">
            <span v-if="item.salonerSender">
              {{ $t(isPhoneNumber(item.salonerSender) ? 'messages_page.history.to_number' : 'messages_page.history.from_override') }}:
              <b class="font-weight-medium text-high-emphasis">{{ isPhoneNumber(item.salonerSender) ? formatPhone(item.salonerSender) : item.salonerSender }}</b>
            </span>
            <span v-if="item.readAt">
              {{ $t('messages_page.history.read_in_panel') }}:
              <b class="font-weight-medium text-high-emphasis">{{ item.readAt }}</b>
            </span>
          </div>
        </template>
      </VDataTableServer>

      <div :class="mobile ? 'pa-3 pt-0' : 'pa-5 pt-0'">
        <TablePaginationComplete
          v-model="page"
          v-model:per-page="perPage"
          class="mt-4"
          :length="Math.ceil((meta.total || 0) / perPage)"
          :page-size-items="[20, 50, 100]"
          show-results-summary
          :filtered-count="meta.total || 0"
          :total-count="meta.totalAll || 0"
          :what="$t('messages_page.history.messages_count')"
        />
      </div>
    </VCard>
  </VContainer>
</template>

<style scoped lang="scss">
#history-tabs {
  .v-tab.v-tab--selected {
    background-color: rgb(var(--v-theme-primary-alpha));
    border-start-start-radius: 8px;
    border-start-end-radius: 8px;
  }
}

// Filters share the row equally - 160px is the narrowest one may get before wrapping
.filter-field {
  min-inline-size: 160px;
}

// Mobile card: lets the status chip shrink below its label width (flex items don't by default)
.mobile-status {
  min-inline-size: 0;

  .v-chip,
  :deep(.v-chip__content) {
    min-inline-size: 0;
  }
}

// Content takes only what the other columns leave and wraps there, so a long SMS never widens the table.
// Same for the mobile card cell - without it the auto table layout grows to the full status label
:deep(.history-message-cell),
.history-mobile-cell {
  inline-size: 100%;
  max-inline-size: 0;
}
</style>
